import type { ActividadPrograma } from "@/interfaces/actividadPrograma.interface";
import {
  Calendar as CalendarIcon,
  FileText,
  Users,
  Download,
  Pencil,
  MoreVertical,
  Clock,
  Play,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { downloadPrivateObject } from "@retro-ia/shared/upload";
import { useState } from "react";

function ActividadAdjuntoDownload({
  archivo,
}: {
  archivo: { llave?: string; nombre?: string; url?: string };
}) {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    if (archivo.llave) {
      setLoading(true);
      try {
        const { url } = await downloadPrivateObject({
          key: archivo.llave,
        });
        window.open(url, "_blank");
      } finally {
        setLoading(false);
      }
    } else if (archivo.url) {
      window.open(archivo.url, "_blank");
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || !(archivo.llave || archivo.url)}
      className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline disabled:opacity-50"
    >
      <Download className="h-4 w-4" />
      {loading ? "..." : archivo.nombre || "Ver archivo adjunto"}
    </button>
  );
}

interface ActivityCardProps {
  actividad: ActividadPrograma;
  aprendicesAsignados: number;
  porcentajeCompletitud?: number;
  onEdit: () => void;
  onReporte: () => void;
  onPreview?: () => void;
  isSuperAdmin?: boolean;
}

const formatActividadDate = (value?: string | Date | null): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { timeZone: "UTC" });
};

const getEstadoActividad = (actividad: ActividadPrograma): string => {
  const now = new Date();
  const fechaInicio = actividad.fechaInicio
    ? new Date(actividad.fechaInicio)
    : null;
  const fechaFin = actividad.fechaFin ? new Date(actividad.fechaFin) : null;

  if (fechaInicio && fechaInicio > now) {
    return "Próximamente";
  } else if (fechaFin && fechaFin < now) {
    return "Finalizada";
  } else {
    return "En curso";
  }
};

const getEstadoVariant = (
  estado: string,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (estado) {
    case "En curso":
      return "default";
    case "Próximamente":
      return "secondary";
    case "Finalizada":
      return "outline";
    default:
      return "secondary";
  }
};

export function ActivityCard({
  actividad,
  aprendicesAsignados,
  porcentajeCompletitud = 0,
  onEdit,
  onReporte,
  onPreview,
  isSuperAdmin = false,
}: ActivityCardProps) {
  const estado = getEstadoActividad(actividad);
  const estadoVariant = getEstadoVariant(estado);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-black/5 bg-[var(--card-background)] p-6 shadow-lg transition-all hover:-translate-y-1 hover:shadow-2xl dark:border-white/10">
      {/* Header con estado y acciones */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Badge
            variant={estadoVariant}
            className={cn(
              "text-xs font-medium",
              (estadoVariant === "default" ||
                estadoVariant === "destructive") &&
                "text-white",
            )}
          >
            {estado}
          </Badge>
          {(actividad.archivoLimpio || actividad.archivoWord) && (
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">Adjuntos</span>
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              Editar actividad
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onReporte}>
              <FileText className="h-4 w-4 mr-2" />
              Generar reporte
            </DropdownMenuItem>
            {isSuperAdmin && onPreview && (
              <DropdownMenuItem onClick={onPreview}>
                <Play className="h-4 w-4 mr-2" />
                Probar actividad
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Título */}
      <h4 className="text-lg font-semibold leading-snug mb-3 text-[var(--foreground)]">
        {actividad.nombre}
      </h4>

      {/* Metadatos visuales */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CalendarIcon className="h-3.5 w-3.5" />
          <span>
            {formatActividadDate(actividad.fechaInicio) || "Sin inicio"} -
            {formatActividadDate(actividad.fechaFin) || "Sin fin"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          <span>
            {aprendicesAsignados} aprendi
            {aprendicesAsignados !== 1 ? "ces" : "z"}
          </span>
        </div>
      </div>

      {/* Indicador de límites de intentos */}
      {actividad.limiteIntentos && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Intentos permitidos
            </span>
            <span className="font-medium">{actividad.limiteIntentos}</span>
          </div>
          {porcentajeCompletitud > 0 && (
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(porcentajeCompletitud, 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Archivos adjuntos: descarga vía URL prefirmada (no usar URL directa S3). */}
      {(actividad.archivoLimpio || actividad.archivoWord) && (
        <div className="mt-4 pt-4 border-t border-black/5 flex flex-col gap-2">
          {actividad.archivoLimpio ? (
            <ActividadAdjuntoDownload archivo={actividad.archivoLimpio} />
          ) : null}
          {actividad.archivoWord ? (
            <ActividadAdjuntoDownload archivo={actividad.archivoWord} />
          ) : null}
        </div>
      )}

      {/* Badge de ID para referencia */}
      <div className="mt-4 pt-2">
        <Badge variant="outline" className="text-[10px] text-muted-foreground">
          ID: {actividad.id}
        </Badge>
      </div>
    </div>
  );
}
